import React, { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Copy, KeySquare, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Settings → Pending Password Resets (Super Admin only).
 *
 * Each row corresponds to an active, unrevealed, unexpired password-reset
 * request. Revealing an entry yields a one-time reset URL that is shown
 * in a modal (with a copy-to-clipboard action) and then permanently
 * disappears from the panel. The raw URL is never persisted on the
 * server, never printed to the browser console, and never included in
 * audit rows.
 */
export default function PendingPasswordResetsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reveal, setReveal] = useState(null); // { row, url }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/admin/pending-resets");
      setRows(Array.isArray(r.data?.pending) ? r.data.pending : []);
    } catch (e) {
      toast.error(errMsg(e, "Unable to load pending resets"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleReveal(row) {
    setBusyId(row.id);
    try {
      const r = await api.post(`/auth/admin/pending-resets/${row.id}/reveal`);
      const url = r.data?.reset_url;
      if (!url) throw new Error("No URL returned");
      setReveal({ row, url });
      // Reload to remove the row from the panel.
      load();
    } catch (e) {
      toast.error(errMsg(e, "Unable to reveal reset URL"));
    } finally {
      setBusyId(null);
    }
  }

  async function copyUrl() {
    if (!reveal?.url) return;
    try {
      await navigator.clipboard.writeText(reveal.url);
      toast.success("Reset URL copied. It will not be shown again.");
    } catch {
      toast.error("Copy failed. Select the URL manually.");
    }
  }

  return (
    <>
      <Card className="mb-6" data-testid="pending-password-resets-card">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div
                className="w-10 h-10 rounded-full grid place-items-center shrink-0"
                style={{ background: "hsl(347 72% 22% / 0.08)", color: "hsl(347 72% 22%)" }}
                aria-hidden="true"
              >
                <ShieldAlert className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg leading-tight">Pending Password Resets</div>
                <div className="text-[12.5px] text-muted-foreground mt-0.5 max-w-2xl leading-relaxed">
                  Active, unrevealed reset requests. Each URL can be revealed only once and
                  will not appear here again. Revealing a URL is audited; the URL itself is
                  never written to any log or audit row.
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="pending-password-resets-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-2" />
              Refresh
            </Button>
          </div>
          <div className="p-5">
            {loading && (
              <div className="text-sm text-muted-foreground" data-testid="pending-password-resets-loading">
                Loading pending resets…
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div
                className="text-sm text-muted-foreground rounded-md border border-dashed p-4"
                data-testid="pending-password-resets-empty"
              >
                No pending password resets. When a user submits a “Forgot password?” request
                and email delivery is unavailable, an entry will appear here for one-time reveal.
              </div>
            )}
            {!loading && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-password-resets-table">
                  <thead className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Account</th>
                      <th className="py-2 pr-4">Role</th>
                      <th className="py-2 pr-4">Requested</th>
                      <th className="py-2 pr-4">Expires</th>
                      <th className="py-2 pr-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t"
                        data-testid={`pending-password-resets-row-${r.id}`}
                      >
                        <td className="py-2 pr-4">
                          <div className="font-medium">{r.target_name || "—"}</div>
                          <div className="text-[12px] text-muted-foreground" data-testid={`pending-password-resets-email-${r.id}`}>
                            {r.target_email || r.email_masked}
                          </div>
                        </td>
                        <td className="py-2 pr-4 text-[12.5px]">{r.target_role || "—"}</td>
                        <td className="py-2 pr-4 text-[12.5px] text-muted-foreground">
                          {formatDate(r.created_at)}
                        </td>
                        <td className="py-2 pr-4 text-[12.5px] text-muted-foreground">
                          {formatDate(r.expires_at)}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          <Button
                            size="sm"
                            onClick={() => handleReveal(r)}
                            disabled={busyId === r.id}
                            data-testid={`pending-password-resets-reveal-${r.id}`}
                            style={{ background: "hsl(347 72% 22%)" }}
                          >
                            <KeySquare className="w-3.5 h-3.5 mr-2" />
                            {busyId === r.id ? "Revealing…" : "Reveal reset URL"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!reveal} onOpenChange={(open) => { if (!open) setReveal(null); }}>
        <DialogContent data-testid="pending-password-resets-reveal-dialog">
          <DialogHeader>
            <DialogTitle>Reset URL revealed — shown once only</DialogTitle>
            <DialogDescription>
              Copy this URL now and deliver it to the account owner through a trusted channel.
              Closing this dialog will hide the URL permanently. This action has been audited.
            </DialogDescription>
          </DialogHeader>
          {reveal && (
            <div className="space-y-3">
              <div className="text-[12.5px] text-muted-foreground">
                Target account:{" "}
                <span className="font-medium text-foreground">
                  {reveal.row?.target_email || reveal.row?.email_masked}
                </span>
              </div>
              <div
                className="rounded-md border p-3 bg-muted text-[12.5px] break-all"
                data-testid="pending-password-resets-reveal-url"
              >
                {reveal.url}
              </div>
              <div className="text-[11.5px] text-muted-foreground">
                URL expires 30 minutes from the original request and can be used only once.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={copyUrl}
              data-testid="pending-password-resets-copy-url"
            >
              <Copy className="w-3.5 h-3.5 mr-2" />
              Copy URL
            </Button>
            <Button
              onClick={() => setReveal(null)}
              data-testid="pending-password-resets-close-reveal"
              style={{ background: "hsl(347 72% 22%)" }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}
